import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, defaultApiParamStr, queryClient } from '@/_shared';

import type {
  MarketingCampaignCreateSchema,
  MarketingCampaignMutationSchema,
  MarketingCampaignQueryAllSchema,
  MarketingCampaignQuerySchema,
} from './schemas';

export function getMarketingCampaignAllQuery(apiParamStr = defaultApiParamStr) {
  return {
    queryKey: ['MARKETING_CAMPAIGNS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<MarketingCampaignQueryAllSchema>({
        endpoint: '/api/v1/marketing-campaigns',
        params: `${apiParamStr}&include=clients,contacts,contactsCount`,
      });

      return res;
    },
  };
}

export function useGetAllMarketingCampaign(apiParamStr: string) {
  return useSuspenseQuery(getMarketingCampaignAllQuery(apiParamStr));
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['MARKETING_CAMPAIGNS'] });
};

export function useGetMarketingCampaign(id: string) {
  return useSuspenseQuery(getMarketingCampaignQuery(id));
}

export function getMarketingCampaignQuery(id: string) {
  return {
    queryKey: ['MARKETING_CAMPAIGN', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: MarketingCampaignQuerySchema }>({
        endpoint: `/api/v1/marketing-campaigns/${id}`,
        params: `include=clients.contacts.profile_image,contacts,attached_files`,
        preventAuditLog: true,
      });

      return res;
    },
  };
}

export const invalidateMarketingCampaignQuery = (id: string) => {
  queryClient.invalidateQueries({ queryKey: ['MARKETING_CAMPAIGN', id] });
};

export const useGetMarketingCampaignMutation: UseControlledMutation<MarketingCampaignQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['MARKETING_CAMPAIGN_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<MarketingCampaignQueryAllSchema>({
            endpoint: '/api/v1/marketing-campaigns',
            params: { search, per_page },
          });

          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export const useCreateMutation = () =>
  useMutation<{ data: MarketingCampaignQuerySchema }, unknown, MarketingCampaignCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/marketing-campaigns/',
        payload,
      }),
  });

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: MarketingCampaignQuerySchema }, ApiError, MarketingCampaignMutationSchema>({
    mutationFn: ({ id: bId, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/marketing-campaigns/${id || bId}`,
        method: 'PUT',
        payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['AUDIT_REVISION_HISTORY', 'marketing-campaigns', id] });
    },
  });

export const usePatchMutation = () =>
  useMutation<{ data: MarketingCampaignQuerySchema }, ApiError, Partial<MarketingCampaignMutationSchema>>({
    mutationFn: ({ id, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/marketing-campaigns/${id}`,
        method: 'PATCH',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['MARKETING_CAMPAIGN_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/marketing-campaigns/${id}`,
        method: 'DELETE',
      }),
  });
